import React, { useState, useEffect } from 'react'

import axios from 'axios'
import { useSelector } from 'react-redux'
import { saveAs } from 'file-saver'

import { Tag, Modal, Upload, Button, Table, Space, Popconfirm, Spin, Input, Select, Typography } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'

import RpcApiClient from 'datasources/RpcApiClient'
import { getEnv } from 'configurations'

const TYCHO_RPC_API = getEnv('REACT_APP_TYCHO_RPC_API')
const TYCHO_DOCUMENTS = getEnv('REACT_APP_TYCHO_DOCUMENTS')

const { Option } = Select
const { Text } = Typography

function Documents(props) {
  const [isModalDocumentVisible, setModalDocumentVisible] = useState(false)
  const [isModalEditDocumentVisible, setModalEditDocumentVisible] = useState(false)
  const [documentSelected, setDocumentSelected] = useState({})
  const [nameSearch, setNameSearch] = useState('')
  const [filesToUpload, setFilesToUpload] = useState({})
  const [uploading, setUploading] = useState(false)

  const [loadingDocuments, setLoadingDocuments] = useState(false)
  const [documents, setDocuments] = useState([])
  const [loadingTags, setLoadingTags] = useState(false)
  const [tags, setTags] = useState([])

  const authData = useSelector((state) => state.auth.authData)

  const getDocuments = (body) => {
    setLoadingDocuments(false)
    RpcApiClient.getDocuments(body).then((data) => {
      setDocuments(data.data.data)
      setLoadingDocuments(true)
    })
  }

  const getTags = () => {
    setLoadingTags(false)
    RpcApiClient.getTags().then((data) => {
      setTags(data.data.data)
      setLoadingTags(true)
    })
  }

  useEffect(() => {
    getDocuments({ text: '' })
    getTags()
  }, [])

  if (loadingTags && loadingDocuments) {
    let tagsDict = tags.reduce((result, filter) => {
      result[filter.tag_id] = filter.name
      return result
    }, {})

    const columns = [
      {
        title: 'Name',
        dataIndex: 'name',
      },
      {
        title: 'Type',
        dataIndex: 'type',
      },
      {
        title: 'Tags',
        dataIndex: 'tags',
        key: 'tags',
        render: (tags) => (
          <span>
            {tags.map((tag) => {
              return <Tag key={tag}>{tagsDict[tag]}</Tag>
            })}
          </span>
        ),
      },
      {
        title: 'Action',
        key: 'action',
        render: (text, record) => (
          <Space size="middle">
            <a
              onClick={() => {
                let link = TYCHO_DOCUMENTS + record.name
                axios({
                  url: link, //your url
                  method: 'GET',
                  responseType: 'blob', // important
                }).then((response) => {
                  const url = window.URL.createObjectURL(new Blob([response.data]))
                  let blob = new Blob([response.data], {
                    type: response.headers['content-type'],
                  })
                  saveAs(blob, record.name)
                })
              }}
            >
              Download
            </a>
            <a href={TYCHO_DOCUMENTS + record.name} without rel="noreferrer" target="_blank">
              Open
            </a>
            <a
              onClick={() => {
                setDocumentSelected({ ...record, original_name: record.name })
                setModalEditDocumentVisible(true)
              }}
            >
              Edit
            </a>
            <Popconfirm
              title="Are you sure to delete this document?"
              onConfirm={() => {
                RpcApiClient.deleteDocument({ document_id: record.document_id }).then(() => {
                  getDocuments({ text: '' })
                })
              }}
              okText="Yes"
              cancelText="No"
            >
              <a>Delete</a>
            </Popconfirm>
          </Space>
        ),
      },
    ]

    return (
      <div style={{ height: '100%' }}>
        <Modal
          title="Edit file"
          visible={isModalEditDocumentVisible}
          footer={null}
          onCancel={() => setModalEditDocumentVisible(false)}
          destroyOnClose={true}
        >
          <div style={{ height: '400px' }}>
            <div>
              <Text>Name</Text>
              <Input
                value={documentSelected.name}
                style={{ marginTop: '5px' }}
                onChange={(e) =>
                  setDocumentSelected({
                    ...documentSelected,
                    name: e.target.value,
                  })
                }
              />
            </div>

            <div style={{ marginTop: '10px' }}>
              <Text>Tags</Text>
              <Select
                mode="multiple"
                allowClear
                style={{ width: '100%' }}
                defaultValue={documentSelected.tags}
                onChange={(value) => setDocumentSelected({ ...documentSelected, tags: value })}
              >
                {tags.map((tag) => (
                  <Option key={tag.tag_id}> {tag.name}</Option>
                ))}
              </Select>
            </div>

            <Button
              type="primary"
              style={{ marginTop: '10px', float: 'right', width: '100%' }}
              onClick={() => {
                let config = {
                  headers: {
                    Authorization: authData.jwtToken,
                    'Content-Type': 'application/json',
                  },
                }
                axios
                  .post(
                    `${TYCHO_RPC_API}/rpc/rename_file`,
                    {
                      name: documentSelected.original_name,
                      new_name: documentSelected.name,
                    },
                    config,
                  )
                  .then((response) => {
                    RpcApiClient.deleteDocument({ document_id: documentSelected.original_name }).then(() => {
                      RpcApiClient.createDocument({
                        document_id: documentSelected.name,
                        name: documentSelected.name,
                        type: documentSelected.type,
                        tags: documentSelected.tags,
                      }).then(() => {
                        setModalEditDocumentVisible(false)
                        getDocuments({ text: '' })
                      })
                    })
                  })
              }}
              disabled={documentSelected.tags === undefined || documentSelected.tags.length === 0}
            >
              Save files
            </Button>
          </div>
        </Modal>

        <Modal
          title="Upload files"
          visible={isModalDocumentVisible}
          footer={null}
          onCancel={() => setModalDocumentVisible(false)}
          destroyOnClose={true}
        >
          <div style={{ height: '400px' }}>
            <div style={{ marginTop: '10px' }}>
              <Text>Tags</Text>
              <Select
                mode="multiple"
                allowClear
                style={{ width: '100%' }}
                defaultValue={documentSelected.tags}
                onChange={(value) => setDocumentSelected({ ...documentSelected, tags: value })}
              >
                {tags.map((tag) => (
                  <Option key={tag.tag_id}> {tag.name}</Option>
                ))}
              </Select>
            </div>

            <div style={{ marginTop: '20px' }}>
              <Upload
                multiple={true}
                showUploadList={true}
                listType="text"
                fileList={Object.values(filesToUpload)}
                onRemove={(file) => {
                  let filesToUploadDict = { ...filesToUpload }
                  delete filesToUploadDict[file.name]
                  setFilesToUpload(filesToUploadDict)
                }}
                beforeUpload={(file, fileList) => {
                  const filesToUploadDict = fileList.reduce((result, filter) => {
                    result[filter.name] = filter
                    return result
                  }, {})

                  setFilesToUpload({ ...filesToUpload, ...filesToUploadDict })
                  return false
                }}
              >
                <Button>Select files</Button>
              </Upload>
            </div>

            <Button
              type="primary"
              style={{ marginTop: '10px', float: 'right', width: '100%' }}
              loading={uploading}
              onClick={() => {
                setUploading(true)

                const presignedUrlsResquests = Object.values(filesToUpload).map((file, index) => {
                  let config = {
                    headers: {
                      Authorization: authData.jwtToken,
                      'Content-Type': 'application/json',
                    },
                  }
                  const presignedUrlRequest = axios.post(
                    `${TYCHO_RPC_API}/rpc/request_signed_url`,
                    { upload_to: file.name, content_type: file.type },
                    config,
                  )

                  return presignedUrlRequest
                })

                axios.all(presignedUrlsResquests).then(
                  axios.spread((...presignetUrlsResponses) => {
                    const uploadDocumentsRequests = presignetUrlsResponses.map((presignetUrlResponse) => {
                      const file_key = presignetUrlResponse.data.upload_url.fields.key
                      const file = filesToUpload[file_key]

                      const formData = new FormData()
                      const fields = presignetUrlResponse.data.upload_url.fields

                      Object.keys(fields).forEach((key) => {
                        formData.append(key, fields[key])
                      })
                      formData.append('file', file)

                      return fetch(presignetUrlResponse.data.upload_url.url, {
                        method: 'POST',
                        body: formData,
                        mode: 'no-cors',
                      })
                    })

                    Promise.all(uploadDocumentsRequests).then((uploadDocumentsResponses) => {
                      const createDocumentRequests = uploadDocumentsResponses.map((uploadDocumentResponse, index) => {
                        const file = Object.values(filesToUpload)[index]
                        const document_id = file.name
                        const name = file.name
                        const type = file.type

                        return RpcApiClient.createDocument({
                          document_id: document_id,
                          name: name,
                          type: type,
                          tags: documentSelected.tags,
                        })
                      })

                      Promise.all(uploadDocumentsRequests).then((uploadDocumentsResponses) => {
                        setTimeout(() => {
                          getDocuments({ text: '' })
                          setUploading(false)
                          setModalDocumentVisible(false)
                          setFilesToUpload({})
                        }, 3000)
                      })
                    })
                  }),
                )
              }}
              disabled={
                documentSelected.tags === undefined ||
                documentSelected.tags.length === 0 ||
                Object.values(filesToUpload).length === 0
              }
            >
              Save files
            </Button>
          </div>
        </Modal>
        <div>
          <div style={{ marginTop: '20px', marginLeft: '20px' }}>
            <Button
              type="primary"
              onClick={() => {
                setDocumentSelected({})
                setModalDocumentVisible(true)
              }}
            >
              Upload Files
            </Button>

            <Input.Search
              enterButton
              style={{ width: '500px', float: 'right' }}
              onSearch={(value) => getDocuments({ text: value })}
            />
          </div>

          <Table columns={columns} dataSource={documents} style={{ marginTop: '20px' }} />
        </div>
      </div>
    )
  } else {
    return (
      <div style={{ height: '100%', width: '100%' }}>
        <Spin style={{ marginTop: '25%' }} tip="Loading..." size="large" indicator={<LoadingOutlined />}>
          <div style={{ height: '100%', width: '100%' }}></div>
        </Spin>
      </div>
    )
  }
}

export default Documents
